import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  DASHBOARD_SCOPE_KEY, EMULATION_HEADER, LOGIN_ROUTE, NAV_SCOPE_KEY, NOT_EMULATING, resolveNav, t,
  type ActivityFeedEntryRow, type EmulationState, type NavGroupRow, type NavItemRow, type ResolvedNav, type ResolvedQueue, type SearchGroup, type SearchHit,
  ACCOUNT_SETTINGS_ROUTE, asDateFormat, asTextSize, asTileListRows, type PendingApprovalsSummary, type TextSize,
  searchSources, settingsSearchHits, TEXT_SIZE_PATH, type SettingsIndexEntryRow } from '@bms2/shared';
import { GlobalHeader, type BuildStamp, type SearchFn, type TestEnvironment } from './GlobalHeader';
import { NavigationBar, NavigationDrawer } from './NavigationBar';
import { useMenuCounts, type MenuCountsAnswer } from './menuCounts';
import { EmulationBanner } from './EmulationBanner';
import { usePasskeyOffer, type PasskeyOffer } from './PasskeyOffer';
import { SessionExpiredBanner } from './SessionExpiredBanner';
import { AlertBanner } from '../components/controls/AlertBanner';
import { AsyncStatePanel } from '../components/composites/AsyncStatePanel';
import { OfflineBanner, ShellErrorBoundary } from './Fallbacks';
import { ViewerZoneProvider } from './ViewerZone';
import { SuperAdminProvider } from './SuperAdmin';
import { StatusResolverProvider, useStatusResolverRead } from './StatusResolver';
import { ApprovedUnitsProvider, useApprovedUnitsRead } from './ApprovedUnits';
import { ViewAsModal, type EmulationTargetRow } from './ViewAsModal';
import { SAVED_VIEWS_PATH, apiFetch, setRequestHeaders, UNAUTHENTICATED_STATUS } from '../pages/records/api';
import { KNOWLEDGE_SEARCH_PATH } from '../pages/knowledge/api';
import { SETTINGS_PATH } from '../pages/settings/api';
import { SettingsIndexRowsProvider } from '../pages/settings/SettingsRail';
import { clearSessionToken, readSessionToken, syncSessionMarker } from '../session';
import { registerCaughtReports, reportBrowserError } from './systemErrors';
import { applyTextSize } from './textSize';
import styles from './shell.module.css';
import { reportCaughtInBrowser } from './systemErrors';

/**
 * The composed shell (piece 12): the Global Header, the navigation bar or its
 * drawer, and whatever page the route resolves to, inside one error boundary.
 * 🔨 The shell and the screens are one stage in two steps: the chrome lands
 * here and the screens land with Record pages, quick-create, list pages,
 * calendar.
 */
/** Who the shell is drawing for, so no screen inside it has to guess (piece 12). */
export interface ShellViewer {
  personId: string;
  /** The viewer's own active employment, which a desk check-in is written against. */
  employmentId?: string;
  name: string;
  initials: string;
  timeZone: string;
  /** The Organization's own zone, which a form date is checked in (T3448); absent in an older answer, which reads the viewer's. */
  organizationTimeZone?: string;
  dateFormat?: string;
  /** The Organization's Tile list rows (decision 5960); absent in an older answer, which reads as four. */
  tileListRows?: number;
  /** The person's own text size, Normal or Large (decision 1321); absent in an older answer, which is read as Normal. */
  textSize?: string;
  /** True when the zone is this person's own setting rather than the Organization's fallback. */
  usesOwnZone?: boolean;
  /** Whether View as is offered to this viewer; absent in an older answer, which offers it (decision 0349). */
  mayViewAs?: boolean;
  /**
   * 🔒 The person the frame is drawing AS under View as (decision 0602), and null
   * otherwise. The header keeps the real person's name; a screen that draws one
   * person's own rows reads this first, which is what makes the calendar the
   * emulated person's rather than the signed-in one's.
   */
  viewingAsPersonId?: string | null;
  /**
   * 🔒 Who View as is drawing, named, from the session row on every read
   * (T725; decision 1497). The banner and its Exit draw from this, so a refresh
   * keeps them; absent in an older answer, where the shell's own choice stands.
   */
  viewingAs?: { employmentId: string; personId: string; personName: string } | null;
  /**
   * 🔒 An external session: a login whose employment is not at the
   * Organization's own Company, or whose address is off every registered company
   * domain (decision 0607). The frame draws the portal for it — no global
   * search, no Settings gear — and the server refuses the rest whatever the
   * frame draws. Absent in an older answer, which is read as internal.
   */
  isExternal?: boolean;
  /** 🔒 The login holds no role, so the frame says why the page is empty (decision 0970); absent in an older answer, which is read as holding one. */
  noRole?: boolean;
  /** 🔒 Decision 7620: the viewer holds the super admin role; absent in an older answer, which is read as not. */
  isSuperAdmin?: boolean;
}

/** One row of the My to-dos tile and the To-Do page (decision 0344), as the API answers it. */
export interface TodoRow {
  id: string;
  title: string;
  description: string | null;
  dueDate: string | null;
  priority: string;
  kind: string;
  sourceType: string;
  sourceRecordId: string | null;
  mine: boolean;
  closesOnTick: boolean;
  route: string | null;
}

export interface TodosAnswer {
  count: number;
  rows: readonly TodoRow[];
}

export interface ShellAnswer {
  viewer?: ShellViewer;
  nav: ResolvedNav;
  navCollapsed: boolean;
  /** The sections the person folded or opened by hand (decision 1441; T692); absent in an older answer, which folds every section but the page's own. */
  navGroups?: Readonly<Record<string, boolean>>;
  queues: readonly ResolvedQueue[];
  tiles: readonly { key: string; label: string }[];
  hiddenTiles?: readonly string[];
  /** The My to-dos tile's count and top rows, present when the viewer passes that tile (decision 0344). */
  todos?: TodosAnswer;
  /** The Pending Approvals tile's numbers, present when the viewer passes that tile (decision 0599). */
  approvals?: PendingApprovalsSummary;
  lastFixtureResetAt: string | null;
}

/**
 * 🔨 In the test environment the header carries a persistent Test environment
 * banner, and the production build carries none (decision 0240). It reads the
 * build stamp and the reset record, never a setting a person can switch, so a
 * production install cannot show it and a test install cannot hide it.
 */
export function testEnvironmentBanner(lastFixtureResetAt: string | null): TestEnvironment | null {
  return __ENV_KIND__ === 'production' ? null : { commit: __COMMIT__, lastResetAt: lastFixtureResetAt };
}

/**
 * 🔨 The running version, read from the same build stamps the banner reads and
 * answered in every environment, production included (decision 0358). The
 * banner says which install this is and stops at production; this says which
 * build it is running, which a person needs most where the banner is absent.
 */
export function runningBuild(): BuildStamp {
  return { commit: __COMMIT__, builtAt: __BUILT_AT__ };
}

export interface AppShellProps {
  apiBaseUrl?: string;
  /** The real person, whose avatar the header keeps for the whole emulation. */
  personId?: string;
  personName?: string;
  initials?: string;
  /** Supplied by the test harness; the shell reads the build stamp otherwise. */
  testEnvironment?: TestEnvironment | null;
  /** Supplied by the test harness, whose build instant would otherwise move with the clock. */
  build?: BuildStamp | null;
  /** Supplied by the test harness; the shell fetches its own otherwise. */
  answer?: ShellAnswer;
  feed?: readonly ActivityFeedEntryRow[];
  /** Supplied by the test harness beside its answer; the frame reads its own otherwise (decision 3072). */
  menuCounts?: MenuCountsAnswer | null;
}

const EMPTY_NAV: ResolvedNav = { pinned: [], groups: [] };

/**
 * 🔨 A path here is relative to the API's own root, and never carries `/api`.
 * `VITE_API_BASE_URL` already names that root — `http://localhost:3000` in
 * development, `https://<host>/api` behind the test environment's proxy, where
 * Caddy strips the prefix before the API sees the request. 💰 Prefixing `/api`
 * here asks for `<host>/api/api/shell`, which 404s, and the shell swallows a
 * failed read: the bar then renders empty and reads as unbuilt rather than as
 * broken (found while building, 2026-09-08).
 */
export const SHELL_PATH = '/shell';
export const EMULATION_TARGETS_PATH = '/shell/emulation-targets';
export const SEARCH_PATH = '/pages/search';
export const TIME_ZONE_PATH = '/shell/time-zone';
export const SIGN_OUT_PATH = '/auth/sign-out';
/** View as under enforcement writes the session row (piece 21; decision 0188). */
export const EMULATE_PATH = '/auth/emulate';
export const EMULATE_EXIT_PATH = '/auth/emulate/exit';

export function AppShell({ apiBaseUrl = '', personId: personIdProp, personName: personNameProp, initials: initialsProp, testEnvironment, build, answer, feed = [], menuCounts: menuCountsProp }: AppShellProps) {
  const [fetched, setFetched] = useState<ShellAnswer | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(answer?.navCollapsed ?? false);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  /** A write the frame makes on the person's behalf that failed; the banner says so rather than letting the tiles snap back. */
  const [shellNotice, setShellNotice] = useState<string | null>(null);
  const [targets, setTargets] = useState<readonly EmulationTargetRow[]>([]);
  /** 🔒 Bumped to read the frame again in place, which Exit does once the session row is cleared (T725; decision 1497). */
  const [frameRead, setFrameRead] = useState(0);
  // A harness-supplied answer is read directly; only the fetched one is state.
  const loaded = answer ?? fetched;
  // T3565: the emulation gate reads `isSuperAdmin` on its actor; what the shell holds is the server's `mayViewAs`, true before the shell loads and never true for a login that may not emulate.
  const mayEmulate = loaded?.viewer?.mayViewAs !== false;
  /**
   * 🔒 The frame reads who it is drawing for once, and every screen inside it
   * takes the person and the zone from here. 💰 The viewer used to be a prop
   * nobody passed, so writes went out with an empty `personId`: the dashboard's
   * arrangement saved nothing, the activity feed was always empty and the
   * calendar's first save was refused at the column (found while building,
   * 2026-09-09).
   */
  const personId = personIdProp ?? loaded?.viewer?.personId ?? '';
  const personName = personNameProp ?? loaded?.viewer?.name ?? 'Developer';
  const initials = initialsProp ?? loaded?.viewer?.initials ?? 'DV';
  const timeZone = loaded?.viewer?.timeZone ?? 'UTC';
  const dateFormat = asDateFormat(loaded?.viewer?.dateFormat);
  const tileListRows = asTileListRows(loaded?.viewer?.tileListRows);
  /**
   * 🔒 The person's own text size (decision 1321): a choice made on Account
   * Settings draws at once, ahead of the server's answer, and the frame's read
   * is what holds it on every later load and every other device.
   */
  const [chosenTextSize, setChosenTextSize] = useState<TextSize | null>(null);
  const textSize: TextSize = chosenTextSize ?? asTextSize(loaded?.viewer?.textSize);
  const viewerLoaded = loaded?.viewer !== undefined;
  useEffect(() => {
    if (viewerLoaded || chosenTextSize !== null) applyTextSize(textSize);
  }, [textSize, viewerLoaded, chosenTextSize]);
  const [emulation, setEmulation] = useState<EmulationState>({ ...NOT_EMULATING, realPersonId: personId });
  /**
   * 🔒 The emulation the frame draws (T725; decision 1497): the shell's own
   * choice while it holds one, which is how the sandbox carries it, and
   * otherwise the one the frame's read names from the session row. 💰 The banner
   * read the shell's memory alone, so a refresh while viewing as an outside login
   * dropped the banner and its Exit, the avatar menu offered no View as, and the
   * administrator was shut inside the portal until they signed out (product
   * owner, 2026-09-30).
   */
  const viewingAs = loaded?.viewer?.viewingAs ?? null;
  const shownEmulation = useMemo<EmulationState>(
    () => (emulation.target || !viewingAs ? emulation : { realPersonId: personId, target: { ...viewingAs, isSuperAdmin: false } }),
    [emulation, viewingAs, personId],
  );

  // 🔨 While no session exists the shell holds the emulated employment and sends
  // it with each request (decision 0188). A request carrying a session resolves
  // the value server-side instead and refuses a client-supplied one.
  /**
   * Global search over the API. 🔨 The in-flight request is cancelled by the
   * signal the header hands in, so a fast typist's earlier answer never
   * overwrites a later one, and a group with no matches never reaches the panel
   * because the server omits it.
   */
  /**
   * 🔒 Decision 2770 (T1616): the Settings group reads the Index rows this viewer passes, once, through the Index's
   * own read, and filters them here; no keystroke asks the server about Settings. The rows are kept per viewer, so
   * View as reads the emulated employment's entries, and a failed read is asked again on the next search.
   */
  const settingsRows = useRef<{ viewer: string; rows: Promise<readonly SettingsIndexEntryRow[]> } | null>(null);
  const readSettingsRows = useCallback(
    (viewer: string): Promise<readonly SettingsIndexEntryRow[]> => {
      if (settingsRows.current?.viewer === viewer) return settingsRows.current.rows;
      const rows = apiFetch(`${apiBaseUrl}${SETTINGS_PATH}/index`)
        .then(async (response) => {
          if (!response.ok) throw new Error('The Settings Index could not be read for the search.');
          return ((await response.json()) as { rows?: SettingsIndexEntryRow[] }).rows ?? [];
        })
        .catch((error: unknown) => {
          settingsRows.current = null;
          reportCaughtInBrowser('shell.search.settings', error);
          return [] as SettingsIndexEntryRow[];
        });
      settingsRows.current = { viewer, rows };
      return rows;
    },
    [apiBaseUrl],
  );
  /**
   * Global search over the API. 🔨 The in-flight request is cancelled by the
   * signal the header hands in, so a fast typist's earlier answer never
   * overwrites a later one, and a group with no matches never reaches the panel
   * because the server omits it. 🔒 The module the header names decides which
   * searches are asked at all (decision 2770).
   */
  const viewerKey = shownEmulation.target?.employmentId ?? personId;
  const runSearch = useCallback<SearchFn>(
    async (term, signal, module) => {
      const sources = searchSources(module);
      const query = encodeURIComponent(term);
      const recordsPath = sources.records ? `${SEARCH_PATH}?q=${query}${sources.records.group ? `&group=${encodeURIComponent(sources.records.group)}` : ''}` : null;
      // 🔒 Piece 30: the Knowledge Base group is answered by its own route, which filters and counts through canViewArticle for this viewer.
      const [response, articles, settings] = await Promise.all([
        recordsPath ? apiFetch(`${apiBaseUrl}${recordsPath}`, { signal }) : Promise.resolve(null),
        sources.knowledge ? apiFetch(`${apiBaseUrl}${KNOWLEDGE_SEARCH_PATH}?q=${query}`, { signal }).catch(/* silent: a fetch the server never answered cannot reach the server's log either; the page says it could not reach the server. */ () => null) : Promise.resolve(null),
        sources.settings ? readSettingsRows(viewerKey).then((rows) => settingsSearchHits(term, rows)) : Promise.resolve(null),
      ]);
      if (response && !response.ok) return { hits: [] };
      type Answer = { hits: SearchHit[]; totals: Record<string, number>; seeAll: Record<string, string> };
      const empty: Answer = { hits: [], totals: {}, seeAll: {} };
      const body = response ? ((await response.json()) as Answer) : empty;
      const kb = articles?.ok ? ((await articles.json()) as Answer) : empty;
      const found: Answer = settings ? { hits: settings.hits, totals: { Settings: settings.total }, seeAll: { Settings: settings.seeAll } } : empty;
      return {
        hits: [...(Array.isArray(body.hits) ? body.hits : []), ...(Array.isArray(kb.hits) ? kb.hits : []), ...found.hits],
        totals: new Map(Object.entries({ ...(body.totals ?? {}), ...(kb.totals ?? {}), ...found.totals })) as ReadonlyMap<SearchGroup, number>,
        seeAll: new Map(Object.entries({ ...(body.seeAll ?? {}), ...(kb.seeAll ?? {}), ...found.seeAll })) as ReadonlyMap<SearchGroup, string>,
      };
    },
    [apiBaseUrl, readSettingsRows, viewerKey],
  );

  const emulationHeaders = useMemo<Record<string, string>>(
    () => (emulation.target ? { [EMULATION_HEADER]: emulation.target.employmentId } : {}),
    [emulation],
  );
  /**
   * 🔨 Every read and write inside the frame carries the emulation, not only the
   * shell's own read (decision 0188). The store is written before the screens
   * fetch, and the outlet is keyed on the emulated employment so every screen
   * asks again as the identity it is now viewing as.
   */
  setRequestHeaders(emulationHeaders);
  useEffect(() => () => setRequestHeaders({}), []);
  // 🔒 Piece 9, decision 0822 (T271): a promise nobody caught is a fault, reported as a row like a render fault is.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const report = (event: PromiseRejectionEvent) => void reportBrowserError(event.reason, apiBaseUrl);
    window.addEventListener('unhandledrejection', report);
    // 🔒 Decision 2200 (T1461): a catch that swallows a failed read or write reports through the same route from here on.
    registerCaughtReports(apiBaseUrl);
    return () => {
      window.removeEventListener('unhandledrejection', report);
      registerCaughtReports(null);
    };
  }, [apiBaseUrl]);

  useEffect(() => {
    if (answer) return;
    const controller = new AbortController();
    apiFetch(`${apiBaseUrl}${SHELL_PATH}`, { signal: controller.signal, headers: emulationHeaders })
      .then((response) => (response.ok ? (response.json() as Promise<ShellAnswer>) : null))
      .then((body) => {
        if (!body || controller.signal.aborted) return;
        setFetched(body);
        setCollapsed(body.navCollapsed);
      })
      .catch((failed: unknown) => {
        reportCaughtInBrowser('shell.preferences.read', failed);
      });
    return () => controller.abort();
  }, [answer, apiBaseUrl, emulationHeaders, frameRead]);

  const nav = loaded?.nav ?? EMPTY_NAV;
  const { pathname } = useLocation();
  // 🔒 Decision 3072 (T2183): the balloons beside the Dispatch Board, My Jobs and Approvals entries, read on each page opened; a harness answer reads none unless it supplies them.
  const countsWanted = [...nav.pinned, ...nav.groups.flatMap((group) => group.items)].some((item) => item.key === 'nav:dispatch' || item.key === 'nav:my-jobs' || item.key === 'nav:approvals');
  const menuCounts = useMenuCounts(apiBaseUrl, countsWanted, pathname, menuCountsProp !== undefined ? menuCountsProp : answer ? null : undefined);
  /** The closed unit list every line-item grid in the frame reads, fetched once here rather than by each document page (decision 0439). */
  const approvedUnits = useApprovedUnitsRead(apiBaseUrl);
  // 🔒 Every pill in the frame reads its label and colour through the one resolver the shell builds from the seeded rows (decision 0639).
  const resolveStatus = useStatusResolverRead(apiBaseUrl);
  /**
   * 🔨 With a session held, View as is the session row's own (decision 0188): the API sets and clears it, and the header rides along for the sandbox alone.
   * 🔒 Exit clears the row first and then reads the frame again in place (T725; decision 1497), so the administrator's own view returns with no refresh.
   * 💰 The read went out beside the clearing write and could answer before it, so Exit left the outside login's frame standing until a refresh (product owner, 2026-09-30).
   * 🔒 A clearing write that fails keeps the shell's memory, reads nothing again and says so, because the row still names the emulation and a re-read would answer it (T725 review).
   */
  const exitEmulation = useCallback(async () => {
    // 🔒 T2291: the session row is cleared whether or not this browser still holds its marker; a 401 to a browser holding none is the sandbox, with no row to clear.
    const marked = readSessionToken() !== null;
    const cleared = await apiFetch(`${apiBaseUrl}${EMULATE_EXIT_PATH}`, { method: 'POST' }, { skipAuthRedirect: !marked }).catch(/* silent: a fetch the server never answered cannot reach the server's log either; the page says it could not reach the server. */ () => null);
    if (!cleared?.ok && !(cleared?.status === UNAUTHENTICATED_STATUS && !marked)) {
      setShellNotice(t('shell.viewAsExitFailed'));
      return;
    }
    setShellNotice((notice) => (notice === t('shell.viewAsExitFailed') ? null : notice));
    setEmulation({ ...NOT_EMULATING, realPersonId: personId });
    setFrameRead((read) => read + 1);
  }, [apiBaseUrl, personId]);
  const navigate = useNavigate();
  /**
   * 🔒 Passkey enrolment, made discoverable (decisions 0493, 1442). It is an OFFER,
   * shown once as a card at the top of the dashboard and a dot on the avatar menu,
   * never a strip over the frame (T693). The frame asks once, so the card and the
   * dot never disagree.
   */
  /**
   * 🔒 Decision 3540 (T2313): a frame opened with no marker asks the server whether its cookie names a session, and a
   * signed-in answer writes the marker back. 💰 A phone that had lost the marker while its cookie lived drew the sandbox's
   * paths: Sign Out ended nothing on the server, and View as took the header the session ignores (found while building PH1).
   */
  const [sessionHeld, setSessionHeld] = useState(() => readSessionToken() !== null);
  useEffect(() => {
    if (answer || readSessionToken() !== null) return undefined;
    let live = true;
    void syncSessionMarker(apiBaseUrl, (url) => apiFetch(url, {}, { skipAuthRedirect: true })).then((held) => {
      if (live && held) setSessionHeld(true);
    });
    return () => {
      live = false;
    };
  }, [answer, apiBaseUrl]);
  const passkeyOffer = usePasskeyOffer({ apiBaseUrl, onOpenSettings: () => navigate(ACCOUNT_SETTINGS_ROUTE), sessionHeld });
  /**
   * 🔨 Sign-out ends the session and the person's presence with it (piece 14):
   * the API revokes the row, the browser drops the token, and the Login Screen
   * is where the person lands. 🔒 It posts whether or not the marker is held
   * (decision 3540; T2313): the server ends the session the cookie names and
   * clears the cookie, and answers alike where none is held, which is the sandbox.
   */
  const signOut = useCallback(async () => {
    await apiFetch(`${apiBaseUrl}${SIGN_OUT_PATH}`, { method: 'POST' }, { skipAuthRedirect: true }).catch(/* silent: a fetch the server never answered cannot reach the server's log either; the page says it could not reach the server. */ () => null);
    clearSessionToken();
    setEmulation({ ...NOT_EMULATING, realPersonId: '' });
    navigate(LOGIN_ROUTE);
  }, [apiBaseUrl, navigate]);

  /** The picker's rows are fetched when it opens, so an unopened menu costs nothing. */
  const openViewAs = useCallback(() => {
    setViewAsOpen(true);
    apiFetch(`${apiBaseUrl}${EMULATION_TARGETS_PATH}`)
      .then((response) => (response.ok ? (response.json() as Promise<{ rows: EmulationTargetRow[] }>) : null))
      .then((body) => setTargets(body?.rows ?? []))
      .catch((failed: unknown) => {
        reportCaughtInBrowser('shell.emulationTargets.read', failed);
        return setTargets([]);
      });
  }, [apiBaseUrl]);

  /**
   * A rearrangement is the person's own `SavedView` row on the dashboard scope
   * (piece 12), which is the one per-user preference store — Tile Canvas held it
   * in memory until this store existed (piece 4, Tile Canvas).
   */
  /**
   * 🔒 The bar's collapsed state and the person's folding of its sections are one
   * row on the nav scope (decision 1441; T692), so each write carries both and
   * neither overwrites the other. A refused write says so, as the dashboard's does.
   */
  const saveNav = useCallback(
    (nextCollapsed: boolean, groups: Readonly<Record<string, boolean>>) => {
      if (!personId) return;
      apiFetch(`${apiBaseUrl}${SAVED_VIEWS_PATH}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json', ...emulationHeaders },
        body: JSON.stringify({ personId, scopeKey: NAV_SCOPE_KEY, layout: { collapsed: nextCollapsed, groups: { ...groups } } }),
      })
        .then((response) => setShellNotice(response.ok ? null : t('shell.navNotSaved')))
        .catch((failed: unknown) => {
          reportCaughtInBrowser('shell.navNotSaved', failed);
          return setShellNotice(t('shell.navNotSaved'));
        });
    },
    [apiBaseUrl, personId, emulationHeaders],
  );
  const [navGroups, setNavGroups] = useState<Readonly<Record<string, boolean>> | null>(null);
  const shownNavGroups = useMemo(() => navGroups ?? loaded?.navGroups ?? {}, [navGroups, loaded]);
  const changeCollapsed = useCallback((next: boolean) => {
    setCollapsed(next);
    saveNav(next, shownNavGroups);
  }, [saveNav, shownNavGroups]);
  const changeNavGroups = useCallback((next: Readonly<Record<string, boolean>>) => {
    setNavGroups(next);
    saveNav(collapsed, next);
  }, [saveNav, collapsed]);

  const saveArrangement = useCallback(
    (order: readonly string[], hidden: readonly string[] = []) => {
      if (!personId) return;
      // 💰 A refused or unreachable save once went unsaid, so the tiles snapped back on the next load (found while building, 2026-09-09).
      apiFetch(`${apiBaseUrl}${SAVED_VIEWS_PATH}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json', ...emulationHeaders },
        body: JSON.stringify({ personId, scopeKey: DASHBOARD_SCOPE_KEY, layout: { order: [...order], hidden: [...hidden] } }),
      })
        .then((response) => setShellNotice(response.ok ? null : t('shell.arrangementNotSaved')))
        .catch((failed: unknown) => {
          reportCaughtInBrowser('shell.arrangementNotSaved', failed);
          return setShellNotice(t('shell.arrangementNotSaved'));
        });
    },
    [apiBaseUrl, personId, emulationHeaders],
  );
  /**
   * 🔨 The zone is written to the viewer's own row and the frame re-reads it,
   * so every screen inside changes clock together rather than one at a time
   * (decision 0261).
   */
  const saveTimeZone = useCallback(
    async (zone: string | null): Promise<{ ok: boolean; message?: string }> => {
      const response = await apiFetch(`${apiBaseUrl}${TIME_ZONE_PATH}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json', ...emulationHeaders },
        body: JSON.stringify({ timeZone: zone }),
      }).catch(/* silent: a fetch the server never answered cannot reach the server's log either; the page says it could not reach the server. */ () => null);
      if (!response || !response.ok) return { ok: false };
      const answer = await apiFetch(`${apiBaseUrl}${SHELL_PATH}`, { headers: emulationHeaders }).catch(/* silent: a fetch the server never answered cannot reach the server's log either; the page says it could not reach the server. */ () => null);
      if (answer?.ok) setFetched((await answer.json()) as ShellAnswer);
      return { ok: true };
    },
    [apiBaseUrl, emulationHeaders],
  );

  /**
   * 🔨 The page changes size the moment the person chooses, and the row is
   * written behind it; a refused write puts the page back and says so, so the
   * screen never shows a size the next load would not.
   */
  const saveTextSize = useCallback(
    async (size: TextSize): Promise<{ ok: boolean }> => {
      const before = textSize;
      setChosenTextSize(size);
      const response = await apiFetch(`${apiBaseUrl}${TEXT_SIZE_PATH}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json', ...emulationHeaders },
        body: JSON.stringify({ textSize: size }),
      }).catch(/* silent: a fetch the server never answered cannot reach the server's log either; the page says it could not reach the server. */ () => null);
      if (!response || !response.ok) {
        setChosenTextSize(before);
        return { ok: false };
      }
      return { ok: true };
    },
    [apiBaseUrl, emulationHeaders, textSize],
  );

  const context = useMemo<ShellOutletContext>(
    () => ({
      personId,
      personName,
      employmentId: loaded?.viewer?.employmentId ?? '',
      timeZone,
      usesOwnZone: loaded?.viewer?.usesOwnZone ?? false,
      saveTimeZone,
      textSize,
      saveTextSize,
      emulating: shownEmulation.target !== null,
      queues: loaded?.queues ?? [],
      tiles: loaded?.tiles ?? [],
      frameLoaded: loaded !== null,
      hiddenTiles: loaded?.hiddenTiles ?? [],
      todos: loaded?.todos ?? null,
      approvals: loaded?.approvals ?? null,
      /**
       * 🔒 Whose own rows a screen inside the frame draws (decision 0602): the
       * emulated person under View as, and the real one otherwise. It comes from
       * the server's answer rather than the picker's click, so it survives a
       * reload while the session's own emulation still stands.
       */
      viewingAsPersonId: loaded?.viewer?.viewingAsPersonId ?? null,
      feed,
      onArrange: saveArrangement,
      passkeyOffer,
    }),
    [personId, personName, timeZone, loaded, feed, saveArrangement, saveTimeZone, textSize, saveTextSize, shownEmulation, passkeyOffer],
  );

  return (
    <div className={styles.frame} data-shell="frame">
      <GlobalHeader
        personName={personName}
        initials={initials}
        search={runSearch}
        testEnvironment={testEnvironment !== undefined ? testEnvironment : testEnvironmentBanner(loaded?.lastFixtureResetAt ?? null)}
        build={build !== undefined ? build : runningBuild()}
        timeZone={timeZone}
        dateFormat={dateFormat}
        onOpenDrawer={() => setDrawerOpen(true)}
        onSignOut={() => void signOut()}
        external={loaded?.viewer?.isExternal === true}
        {...(loaded?.viewer?.mayViewAs === false ? {} : { onViewAs: openViewAs })}
        onAccountSettings={() => navigate(ACCOUNT_SETTINGS_ROUTE)}
        securityDot={passkeyOffer.offered}
      />
      <SessionExpiredBanner apiBaseUrl={apiBaseUrl} />
      {/* 🔒 Decision 0970: a login holding no role reads nothing, and the frame says so in the invite window's own sentence. */}
      {loaded?.viewer?.noRole === true ? <div data-shell-no-role="true"><AlertBanner tone="inactive" message={t('settings.logins.noRoleWarning')} /></div> : null}
      {shellNotice ? <AlertBanner tone="error" message={shellNotice} action={{ label: t('modal.dismiss'), onAction: () => setShellNotice(null) }} /> : null}
      <ViewAsModal
        open={viewAsOpen}
        actor={{ personId, isSuperAdmin: mayEmulate }}
        rows={targets}
        onChoose={(target) => {
          const chosen = () => {
            setEmulation({ realPersonId: personId, target });
            setViewAsOpen(false);
          };
          /**
           * 🔒 T2291: the server says whether a session holds View as, never the browser's marker alone. A phone whose
           * marker was gone while its session cookie lived took the sandbox's header path, which the session ignores:
           * the banner named the person and the drawer drew the administrator's whole menu. A 401 to a browser holding
           * no marker is the sandbox, which carries the choice in the header.
           */
          const marked = readSessionToken() !== null;
          apiFetch(`${apiBaseUrl}${EMULATE_PATH}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ employmentId: target.employmentId }) }, { skipAuthRedirect: !marked })
            .then(async (response) => {
              if (response.ok || (response.status === UNAUTHENTICATED_STATUS && !marked)) return chosen();
              const body = (await response.json().catch(/* silent: a body that is not JSON reads as none, and the status check beside it answers the failure. */ () => null)) as { message?: string } | null;
              setShellNotice(body?.message ?? t('shell.viewAsRefused'));
              setViewAsOpen(false);
            })
            .catch((failed: unknown) => {
              reportCaughtInBrowser('shell.viewAs', failed);
              return setShellNotice(t('shell.viewAsRefused'));
            });
        }}
        onCancel={() => setViewAsOpen(false)}
      />
      <EmulationBanner state={shownEmulation} onExit={() => void exitEmulation()} />
      <OfflineBanner />
      <NavigationDrawer nav={nav} open={drawerOpen} onClose={() => setDrawerOpen(false)} counts={menuCounts} />
      {/* 🔨 The bar and the page share what the header and any banner leave, and
          the page's own Content region is the only thing in the frame that
          scrolls (piece 12). */}
      <div className={styles.body} data-shell="body">
        <NavigationBar nav={nav} collapsed={collapsed} onCollapsedChange={changeCollapsed} groups={shownNavGroups} onGroupsChange={changeNavGroups} showSettings={loaded?.viewer?.isExternal !== true} counts={menuCounts} />
        <ShellErrorBoundary apiBaseUrl={apiBaseUrl}>
          <ViewerZoneProvider timeZone={timeZone} organizationTimeZone={loaded?.viewer?.organizationTimeZone ?? timeZone} dateFormat={dateFormat} tileListRows={tileListRows}>
            {/* Every line-item grid inside the frame reads the approved units from here, so no document page asks for them itself (decision 0439). */}
            <ApprovedUnitsProvider units={approvedUnits}>
              <StatusResolverProvider resolve={resolveStatus}>
                <div key={shownEmulation.target?.employmentId ?? 'self'} className={styles.outlet} data-shell="outlet" data-emulating={shownEmulation.target ? 'true' : 'false'}>
                  {/* 🔒 Every Settings page's rail reads the Index rows from here, once a viewer, not once a page (decision 2792). */}
                  <SettingsIndexRowsProvider apiBaseUrl={apiBaseUrl}>
                    {/* 🔒 Decision 7620: the proving reads draw for a super admin alone, from the frame's own answer. */}
                    <SuperAdminProvider superAdmin={loaded?.viewer?.isSuperAdmin === true}>
                      {/* 🔒 Decision 10580: a route group's page loads on its first visit, and the outlet draws the one loading state every page's first read draws while it arrives. */}
                      <Suspense fallback={<PageArriving />}>
                        <Outlet context={context} />
                      </Suspense>
                    </SuperAdminProvider>
                  </SettingsIndexRowsProvider>
                </div>
              </StatusResolverProvider>
            </ApprovedUnitsProvider>
          </ViewerZoneProvider>
        </ShellErrorBoundary>
      </div>
    </div>
  );
}

/**
 * The loading state a page's own first read draws, shown while a route group's chunk arrives (decision 10580).
 * The panel draws no Try again while it loads; were it ever to, trying again is loading the page afresh.
 */
function PageArriving() {
  return (
    <div data-shell-page-arriving="true">
      <AsyncStatePanel state={{ kind: 'loading' }} emptyAction="" onRetry={() => window.location.reload()}>
        {() => null}
      </AsyncStatePanel>
    </div>
  );
}

/** What a page inside the shell reads from it, rather than fetching the registries again. */
export interface ShellOutletContext {
  /** The viewer every screen attributes its writes to, until a session names one. */
  personId: string;
  personName: string;
  /** The viewer's own active employment, or empty for a person with no job. */
  employmentId: string;
  /** True while View as is on, so a screen can say so beside a write. */
  emulating: boolean;
  /** The zone a screen renders its times in; a record's own zone is named beside it wherever the two differ. */
  timeZone: string;
  /** True while the viewer holds a zone row of their own rather than the Organization's fallback. */
  usesOwnZone: boolean;
  /** Account Settings writes the viewer's own zone through the frame, so every screen changes clock together (decision 0261). */
  saveTimeZone: (zone: string | null) => Promise<{ ok: boolean; message?: string }>;
  /** The person's own text size, and its write: the page redraws at once and the row follows (decision 1321). */
  textSize: TextSize;
  saveTextSize: (size: TextSize) => Promise<{ ok: boolean }>;
  queues: readonly ResolvedQueue[];
  tiles: readonly { key: string; label: string }[];
  /** False until the frame's first read answers, so the Dashboard says nothing about an empty tile list it has not yet read (T725 review). */
  frameLoaded: boolean;
  /** The tiles the viewer removed from their dashboard; the library offers them back (decision 0264). */
  hiddenTiles: readonly string[];
  /** The My to-dos tile's rows, or null for a viewer who does not pass that tile (decision 0344). */
  todos: TodosAnswer | null;
  /** The Pending Approvals tile's numbers, or null for a viewer who does not pass that tile (decision 0599). */
  approvals: PendingApprovalsSummary | null;
  /** The person the frame is drawing as under View as, null otherwise; a screen drawing one person's own rows reads it (decision 0602). */
  viewingAsPersonId: string | null;
  feed: readonly ActivityFeedEntryRow[];
  /** A rearrangement, or a tile added or removed, writes the viewer's own SavedView row and changes nobody else's. */
  onArrange: (order: readonly string[], hidden: readonly string[]) => void;
  /** 🔒 The passkey offer the frame asked once (decision 1442; T693): the dashboard draws its card and the avatar menu its dot. */
  passkeyOffer: PasskeyOffer;
}

/** Re-exported so a screen imports one name and not five. */
export { resolveNav };
export type { NavGroupRow, NavItemRow };
